import { useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  OBJECT_TYPES,
  MODES,
  GRID_CELL_SIZE_M,
  ELECTRICAL_ELEMENT_TYPES,
  SANITARY_ELEMENT_TYPES,
} from '../configuratorConstants';
import { getObjectById } from '../state/projectReducer';
import { distance, subtractVectors, findNearestPointOnLineSegment } from '../utils/geometry';
import {
  isSanitaryJoint,
  isEdgeSegmentInSanitaryJoint,
} from '../utils/sanitaryCornerUtils';

export const useElementPlacement = ({
  elementTypeToPlace,
  setElementTypeToPlace,
  state,
  dispatch,
  currentFloorIndex,
  t,
  objectsOnCurrentFloor,
  availableTemplates,
  objectsToRender,
  hasRooms,
}) => {
  const checkForWindowNearby = useCallback((segmentObject, objectsOnCurrentFloor) => {
    if (segmentObject.type === OBJECT_TYPES.WALL_SEGMENT) {
      // Проверяем элементы на этом сегменте стены
      const elements = segmentObject.elements || [];
      const hasWindow = elements.some(el =>
        el.type === OBJECT_TYPES.WINDOW || el.type === OBJECT_TYPES.PANORAMIC_WINDOW
      );
      return hasWindow;
    } else if (segmentObject.type === OBJECT_TYPES.DOCKING_JOINT) {
      // Для стыков проверяем элементы на стыке
      const elements = segmentObject.elements || [];
      const hasWindow = elements.some(el =>
        el.type === OBJECT_TYPES.WINDOW || el.type === OBJECT_TYPES.PANORAMIC_WINDOW
      );
      return hasWindow;
    } else if (segmentObject.type === 'frameless_wall_segment' ||
      segmentObject.type === OBJECT_TYPES.FRAMELESS_WALL ||
      segmentObject.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
      // Для безкаркасных стен и перегородок
      let segment = null;

      if (segmentObject.type === 'frameless_wall_segment') {
        // Находим родительскую стену и сегмент
        const parentWallId = segmentObject.parentWallId;
        if (parentWallId) {
          const wall = objectsOnCurrentFloor.find(obj =>
            (obj.type === OBJECT_TYPES.FRAMELESS_WALL || obj.type === OBJECT_TYPES.FRAMELESS_PARTITION) &&
            obj.id === parentWallId
          );
          if (wall && wall.segments) {
            segment = wall.segments.find(seg => seg.id === segmentObject.id);
          }
        }
      } else if (segmentObject.segments) {
        // Если это сама стена, проверяем все сегменты
        for (const seg of segmentObject.segments) {
          const elements = seg.elements || [];
          const hasWindow = elements.some(el =>
            el.type === OBJECT_TYPES.WINDOW || el.type === OBJECT_TYPES.PANORAMIC_WINDOW
          );
          if (hasWindow) return true;
        }
        return false;
      }

      if (segment && segment.elements) {
        const hasWindow = segment.elements.some(el =>
          el.type === OBJECT_TYPES.WINDOW || el.type === OBJECT_TYPES.PANORAMIC_WINDOW
        );
        return hasWindow;
      }
    }
    return false;
  }, []);

  // --- НАЧАЛО ИЗМЕНЕНИЙ ---
  const handleWallSegmentClickForPlacement = useCallback(
    (segmentId, options = {}) => {
      if (
        !elementTypeToPlace ||
        elementTypeToPlace === OBJECT_TYPES.FRAMELESS_WALL_TOOL
      )
        return;

      const segmentObject = getObjectById(
        segmentId,
        state.currentFloorId,
        state.projectObjects,
      );
      if (!segmentObject) {
        setElementTypeToPlace(null);
        return;
      }

      // Проверяем, является ли сегмент частью перегородки
      if (segmentObject.parentPartitionId || segmentObject.parentWallId) {
        const parentId = segmentObject.parentPartitionId || segmentObject.parentWallId;
        const parentObject = getObjectById(
          parentId,
          state.currentFloorId,
          state.projectObjects,
        );

        if (parentObject && parentObject.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
          if (elementTypeToPlace === OBJECT_TYPES.PANORAMIC_WINDOW) {
            toast.error("Не можна розміщувати панорамне вікно на перегородці");
            setElementTypeToPlace(null);
            return;
          }
          if (elementTypeToPlace === OBJECT_TYPES.GATE) {
            toast.error("Не можна розміщувати ворота на перегородці");
            setElementTypeToPlace(null);
            return;
          }
        }
      }

      // Также проверяем, если сам объект является перегородкой
      if (segmentObject.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
        if (elementTypeToPlace === OBJECT_TYPES.PANORAMIC_WINDOW) {
          toast.error("Не можна розміщувати панорамне вікно на перегородці");
          setElementTypeToPlace(null);
          return;
        }
        if (elementTypeToPlace === OBJECT_TYPES.GATE) {
          toast.error("Не можна розміщувати ворота на перегородці");
          setElementTypeToPlace(null);
          return;
        }
      }

      // Специальная обработка для жалюзи - размещаем на том же месте, что и окно
      if (elementTypeToPlace === OBJECT_TYPES.WINDOW_BLINDS) {
        if (options.isBlindsPlacement && options.windowElement) {
          // Обработка для модульного режима
          if (segmentObject.type === OBJECT_TYPES.WALL_SEGMENT && segmentObject.parentModuleId) {
            const module = objectsOnCurrentFloor.find(obj =>
              obj.type === OBJECT_TYPES.MODULE &&
              obj.wallSegments &&
              Object.values(obj.wallSegments).some(ws => ws.id === segmentId)
            );

            if (module && module.wallSegments) {
              const wallSegment = Object.values(module.wallSegments).find(ws => ws.id === segmentId);
              if (wallSegment && wallSegment.elements) {
                const windowElement = wallSegment.elements.find(el => el.id === options.windowElement.id);
                if (windowElement) {
                  // Используем позицию окна для размещения жалюзи
                  options.positionOnSegment = windowElement.positionOnSegment || 0.5;
                  options.clickSide = windowElement.placementSide || 1;
                }
              }
            }
          }
          // Обработка для безкаркасного режима
          else if (segmentObject.type === 'frameless_wall_segment' ||
            (segmentObject.type === OBJECT_TYPES.FRAMELESS_WALL || segmentObject.type === OBJECT_TYPES.FRAMELESS_PARTITION)) {
            // Находим стену и сегмент
            let wall = null;
            let segment = null;

            // Используем parentWallId из options.segmentData, если он есть
            const parentWallId = options.segmentData?.parentWallId;

            if (parentWallId) {
              wall = objectsOnCurrentFloor.find(obj =>
                (obj.type === OBJECT_TYPES.FRAMELESS_WALL || obj.type === OBJECT_TYPES.FRAMELESS_PARTITION) &&
                obj.id === parentWallId
              );

              if (wall && wall.segments) {
                segment = wall.segments.find(seg => seg.id === segmentId);
              }
            } else if (segmentObject.type === OBJECT_TYPES.FRAMELESS_WALL || segmentObject.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
              // Если segmentObject - это сама стена
              wall = segmentObject;
              if (wall.segments) {
                segment = wall.segments.find(seg => seg.id === segmentId);
              }
            } else if (segmentObject.type === 'frameless_wall_segment') {
              // Если segmentObject - это сегмент, ищем родительскую стену
              // Ищем стену, которая содержит этот сегмент
              wall = objectsOnCurrentFloor.find(obj =>
                (obj.type === OBJECT_TYPES.FRAMELESS_WALL || obj.type === OBJECT_TYPES.FRAMELESS_PARTITION) &&
                obj.segments &&
                obj.segments.some(seg => seg.id === segmentId)
              );

              if (wall && wall.segments) {
                segment = wall.segments.find(seg => seg.id === segmentId);
              }
            }

            if (segment && segment.elements) {
              const windowElement = segment.elements.find(el => el.id === options.windowElement.id);
              if (windowElement) {
                // Используем позицию окна для размещения жалюзи
                options.positionOnSegment = windowElement.positionOnSegment || 0.5;
                options.clickSide = windowElement.placementSide || 1;

              }
            }
          }
        }
      }

      // Новая логика для точного позиционирования на безкаркасных стенах
      // НЕ применяем для ролеты, если позиция уже установлена из окна
      let finalPositionOnSegment = options.positionOnSegment;
      let finalSegmentIndex = options.segmentIndex;
      if (
        segmentObject &&
        (segmentObject.type === "frameless_wall_segment" ||
          segmentObject.type === OBJECT_TYPES.FRAMELESS_PARTITION) &&
        segmentObject.parentWallId &&
        !(elementTypeToPlace === OBJECT_TYPES.WINDOW_BLINDS && options.isBlindsPlacement && options.positionOnSegment !== undefined)
      ) {
        const parentWall = getObjectById(
          segmentObject.parentWallId,
          state.currentFloorId,
          state.projectObjects,
        );
        if (
          parentWall &&
          parentWall.startPoint &&
          parentWall.endPoint &&
          options.worldCoords
        ) {
          const { t: relativePosition } = findNearestPointOnLineSegment(
            options.worldCoords,
            parentWall.startPoint,
            parentWall.endPoint,
          );

          finalSegmentIndex = null;

          if (
            typeof segmentObject.start === "number" &&
            typeof segmentObject.length === "number" &&
            segmentObject.length > 0
          ) {
            const wallLengthMeters = distance(
              parentWall.startPoint,
              parentWall.endPoint,
            );

            if (wallLengthMeters > 0) {
              const absoluteOffsetMeters = relativePosition * wallLengthMeters;
              const localPositionOnSegment =
                (absoluteOffsetMeters - segmentObject.start) /
                segmentObject.length;
              finalPositionOnSegment = Math.max(
                0,
                Math.min(1, localPositionOnSegment),
              );
            } else {
              finalPositionOnSegment = relativePosition;
            }
          } else {
            finalPositionOnSegment = relativePosition;
          }
        }
      }
      // --- КОНЕЦ ИЗМЕНЕНИЙ ---


      // Проверяем, является ли сегмент запрещенным в шаблоне модуля
      if (segmentObject.type === OBJECT_TYPES.WALL_SEGMENT &&
        segmentObject.parentModuleId &&
        segmentObject.segmentKeyInParentModule) {

        const parentModule = objectsOnCurrentFloor.find(
          obj => obj.id === segmentObject.parentModuleId && obj.type === OBJECT_TYPES.MODULE
        );

        if (parentModule && parentModule.templateId) {
          const moduleTemplate = availableTemplates.find(
            template => template.config?.id === parentModule.templateId
          );

          if (moduleTemplate && moduleTemplate.config?.disallowedWallSegments) {
            const isDisallowed = moduleTemplate.config.disallowedWallSegments.includes(
              segmentObject.segmentKeyInParentModule
            );

            if (isDisallowed) {
              toast.error("На цьому сегменті заборонено розміщувати елементи через план модуля");
              setElementTypeToPlace(null);
              return;
            }
          }
        }
      }

      // Проверяем, является ли внутренний сегмент стены запрещенным
      if (segmentObject.type === OBJECT_TYPES.WALL_SEGMENT &&
        segmentObject.isInternal &&
        segmentObject.segmentKeyInModule) {

        const parentModule = objectsOnCurrentFloor.find(
          obj => obj.id === segmentObject.moduleId && obj.type === OBJECT_TYPES.MODULE
        );

        if (parentModule && parentModule.templateId) {
          const moduleTemplate = availableTemplates.find(
            template => template.config?.id === parentModule.templateId
          );

          if (moduleTemplate && moduleTemplate.config?.disallowedWallSegments) {
            const isDisallowed = moduleTemplate.config.disallowedWallSegments.includes(
              segmentObject.segmentKeyInModule
            );

            if (isDisallowed) {
              toast.error("На цьому сегменті заборонено розміщувати елементи через план модуля");
              setElementTypeToPlace(null);
              return;
            }
          }
        }
      }

      // ВАЖНО: disallowedWallSegments применяются только к сегментам стен внутри модуля,
      // а не к стыкам между модулями. Стыки (DOCKING_JOINT) - это отдельные области между модулями,
      // где можно размещать элементы независимо от disallowedWallSegments модулей.
      // Проверка для стыков удалена, так как она неправильно запрещала размещение элементов
      // на стыках лестничных модулей и других модулей с disallowedWallSegments.

      // Специальная обработка для жалюзи - размещаем на том же месте, что и окно
      if (elementTypeToPlace === OBJECT_TYPES.WINDOW_BLINDS) {
        if (options.isBlindsPlacement && options.windowElement) {
          // Обработка для модульного режима
          if (segmentObject.type === OBJECT_TYPES.WALL_SEGMENT && segmentObject.parentModuleId) {
            const module = objectsOnCurrentFloor.find(obj =>
              obj.type === OBJECT_TYPES.MODULE &&
              obj.wallSegments &&
              Object.values(obj.wallSegments).some(ws => ws.id === segmentId)
            );

            if (module && module.wallSegments) {
              const wallSegment = Object.values(module.wallSegments).find(ws => ws.id === segmentId);
              if (wallSegment && wallSegment.elements) {
                const windowElement = wallSegment.elements.find(el => el.id === options.windowElement.id);
                if (windowElement) {
                  // Используем позицию окна для размещения жалюзи
                  options.positionOnSegment = windowElement.positionOnSegment || 0.5;
                  options.clickSide = windowElement.placementSide || 1;
                }
              }
            }
          }
          // Обработка для безкаркасного режима
          else if (segmentObject.type === 'frameless_wall_segment' ||
            (segmentObject.type === OBJECT_TYPES.FRAMELESS_WALL || segmentObject.type === OBJECT_TYPES.FRAMELESS_PARTITION)) {
            // Находим стену и сегмент
            let wall = null;
            let segment = null;

            // Используем parentWallId из options.segmentData, если он есть
            const parentWallId = options.segmentData?.parentWallId;

            if (parentWallId) {
              wall = objectsOnCurrentFloor.find(obj =>
                (obj.type === OBJECT_TYPES.FRAMELESS_WALL || obj.type === OBJECT_TYPES.FRAMELESS_PARTITION) &&
                obj.id === parentWallId
              );

              if (wall && wall.segments) {
                segment = wall.segments.find(seg => seg.id === segmentId);
              }
            } else if (segmentObject.type === OBJECT_TYPES.FRAMELESS_WALL || segmentObject.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
              // Если segmentObject - это сама стена
              wall = segmentObject;
              if (wall.segments) {
                segment = wall.segments.find(seg => seg.id === segmentId);
              }
            } else if (segmentObject.type === 'frameless_wall_segment') {
              // Если segmentObject - это сегмент, ищем родительскую стену
              // Ищем стену, которая содержит этот сегмент
              wall = objectsOnCurrentFloor.find(obj =>
                (obj.type === OBJECT_TYPES.FRAMELESS_WALL || obj.type === OBJECT_TYPES.FRAMELESS_PARTITION) &&
                obj.segments &&
                obj.segments.some(seg => seg.id === segmentId)
              );

              if (wall && wall.segments) {
                segment = wall.segments.find(seg => seg.id === segmentId);
              }
            }

            if (segment && segment.elements) {
              const windowElement = segment.elements.find(el => el.id === options.windowElement.id);
              if (windowElement) {
                // Используем позицию окна для размещения жалюзи
                options.positionOnSegment = windowElement.positionOnSegment || 0.5;
                options.clickSide = windowElement.placementSide || 1;

              }
            }
          }
        } else {
          // Если клик был не по окну, проверяем наличие окна рядом
          const hasWindowNearby = checkForWindowNearby(segmentObject, objectsOnCurrentFloor);
          if (!hasWindowNearby) {
            toast.error("Жалюзі можна розміщувати тільки поруч з вікнами");
            setElementTypeToPlace(null);
            return;
          }
        }
      }

      if (elementTypeToPlace === OBJECT_TYPES.INTERNAL_WALL_TOOL) {
        // Обработка для модульного режима
        if (
          segmentObject.type === OBJECT_TYPES.DOCKING_JOINT &&
          (segmentObject.displayMode === "passage" ||
            segmentObject.displayMode === "custom")
        ) {
          dispatch({
            type: "TOGGLE_WALL_SEGMENT_IN_JOINT",
            payload: {
              jointId: segmentObject.id,
              positionOnSegment: options.positionOnSegment,
            },
          });
          return;
        }

        // Обработка для безкаркасного режима - создание внутренней стены внутри комнаты
        if (state.activeMode === MODES.FRAMELESS) {
          // Проверяем, что сегмент находится внутри комнаты
          if (segmentObject.type === 'frameless_wall_segment' ||
            segmentObject.type === OBJECT_TYPES.FRAMELESS_WALL ||
            segmentObject.type === OBJECT_TYPES.FRAMELESS_PARTITION) {

            // Находим родительскую стену
            const parentWallId = segmentObject.parentWallId || segmentObject.id;
            const parentWall = objectsOnCurrentFloor.find(obj =>
              (obj.type === OBJECT_TYPES.FRAMELESS_WALL || obj.type === OBJECT_TYPES.FRAMELESS_PARTITION) &&
              obj.id === parentWallId
            );

            if (parentWall) {
              // Проверяем, что стена находится внутри комнаты
              // Для этого проверяем, что стена является частью замкнутого цикла
              const allWalls = objectsToRender.filter(
                (obj) => obj.type === OBJECT_TYPES.FRAMELESS_WALL
              );

              // Упрощенная проверка: если есть комнаты, разрешаем создание внутренних стен
              if (hasRooms) {
                // Создаем внутреннюю стену (перегородку) на месте сегмента
                // Используем координаты сегмента для создания новой перегородки
                const segment = parentWall.segments?.find(seg => seg.id === segmentId);
                if (segment && parentWall.startPoint && parentWall.endPoint) {
                  const wallLength = distance(parentWall.startPoint, parentWall.endPoint);
                  const segmentStartM = segment.start;
                  const segmentEndM = segment.start + segment.length;

                  // Вычисляем координаты начала и конца сегмента
                  const wallVector = subtractVectors(parentWall.endPoint, parentWall.startPoint);
                  const wallAngle = Math.atan2(wallVector.y, wallVector.x);

                  const segmentStartPoint = {
                    x: parentWall.startPoint.x + Math.cos(wallAngle) * segmentStartM,
                    y: parentWall.startPoint.y + Math.sin(wallAngle) * segmentStartM
                  };

                  const segmentEndPoint = {
                    x: parentWall.startPoint.x + Math.cos(wallAngle) * segmentEndM,
                    y: parentWall.startPoint.y + Math.sin(wallAngle) * segmentEndM
                  };

                  // Создаем внутреннюю стену перпендикулярно к стене, идущую внутрь комнаты
                  // Внутренняя стена размещается на месте сегмента, где могут быть элементы (сендвичи)
                  const centerPoint = {
                    x: (segmentStartPoint.x + segmentEndPoint.x) / 2,
                    y: (segmentStartPoint.y + segmentEndPoint.y) / 2
                  };

                  // Определяем направление стены и создаем перпендикулярную перегородку
                  const dx = Math.abs(segmentEndPoint.x - segmentStartPoint.x);
                  const dy = Math.abs(segmentEndPoint.y - segmentStartPoint.y);
                  const isWallHorizontal = dy < 0.01;

                  // Длина внутренней стены по умолчанию (можно будет изменять)
                  const internalWallLength = 1.0; // 1 метр

                  let partitionStart, partitionEnd;

                  if (isWallHorizontal) {
                    // Стена горизонтальная, перегородка вертикальная (внутрь комнаты)
                    partitionStart = {
                      x: centerPoint.x,
                      y: centerPoint.y - internalWallLength / 2
                    };
                    partitionEnd = {
                      x: centerPoint.x,
                      y: centerPoint.y + internalWallLength / 2
                    };
                  } else {
                    // Стена вертикальная, перегородка горизонтальная (внутрь комнаты)
                    partitionStart = {
                      x: centerPoint.x - internalWallLength / 2,
                      y: centerPoint.y
                    };
                    partitionEnd = {
                      x: centerPoint.x + internalWallLength / 2,
                      y: centerPoint.y
                    };
                  }

                  dispatch({
                    type: "CREATE_FRAMELESS_INTERNAL_WALL",
                    payload: {
                      startPoint: partitionStart,
                      endPoint: partitionEnd,
                      parentRoomId: parentWallId,
                      parentWallId: parentWallId,
                      segmentId: segmentId
                    }
                  });

                  setElementTypeToPlace(null);
                  return;
                }
              } else {
                toast.error("Внутренні стіни можна створювати тільки всередині кімнат");
                setElementTypeToPlace(null);
                return;
              }
            }
          }
        }
        return;
      }

      if (elementTypeToPlace === OBJECT_TYPES.DOOR && currentFloorIndex > 0) {
        if (
          segmentObject.type === OBJECT_TYPES.WALL_SEGMENT &&
          segmentObject.parentModuleId
        ) {
          const isExternalWall =
            (segmentObject.orientation === "h" &&
              (segmentObject.cellY === 0 ||
                segmentObject.cellY === segmentObject.parentModuleCellsLong)) ||
            (segmentObject.orientation === "v" &&
              (segmentObject.cellX === 0 ||
                segmentObject.cellX === segmentObject.parentModuleCellsWide));

          if (isExternalWall) {
            toast.error(t("toasts.doorPlacementOnExteriorWallError"));
            setElementTypeToPlace(null);
            return;
          }
        }
      }

      if (segmentObject.type === OBJECT_TYPES.DOCKING_JOINT) {
        const joint = segmentObject;
        const numSlots = Math.max(1, Math.round(joint.width / GRID_CELL_SIZE_M));

        if (numSlots > 0) {
          // Панорамное окно занимает 2 соседних слота
          const isPanoramicWindow = elementTypeToPlace === OBJECT_TYPES.PANORAMIC_WINDOW;
          const slotsNeeded = isPanoramicWindow ? 2 : 1;

          const normalizedSegmentIndex =
            finalSegmentIndex === null ||
              finalSegmentIndex === undefined ||
              Number.isNaN(finalSegmentIndex)
              ? Math.max(
                0,
                Math.min(
                  numSlots - slotsNeeded,
                  Math.floor((finalPositionOnSegment ?? 0.5) * numSlots),
                ),
              )
              : Math.max(
                0,
                Math.min(numSlots - slotsNeeded, Math.floor(finalSegmentIndex)),
              );

          finalSegmentIndex = normalizedSegmentIndex;
          const targetSlotIndex = normalizedSegmentIndex;

          if (isPanoramicWindow) {
            // Центрируем панорамное окно на 2 слотах
            finalPositionOnSegment = (targetSlotIndex + 1) / numSlots;

            // Проверяем электрику на обоих слотах, которые займет панорамное окно
            const slotsToCheck = [targetSlotIndex, targetSlotIndex + 1];
            const hasElectricalOnSlots = slotsToCheck.some(slotIndex => {
              return joint.elements?.some((exEl) => {
                if (!ELECTRICAL_ELEMENT_TYPES.includes(exEl.type)) return false;
                const exElSlotIndex = Math.max(
                  0,
                  Math.min(
                    numSlots - 1,
                    Math.floor((exEl.positionOnSegment ?? 0.5) * numSlots),
                  ),
                );
                return exElSlotIndex === slotIndex;
              });
            });

            if (hasElectricalOnSlots) {
              toast.error("Не можна розміщувати панорамне вікно: на цьому місці вже є електрика");
              setElementTypeToPlace(null);
              return;
            }
          } else {
            const slotCenter = (targetSlotIndex + 0.5) / numSlots;
            finalPositionOnSegment = slotCenter;
          }

          if (SANITARY_ELEMENT_TYPES.includes(elementTypeToPlace)) {
            const isTargetSlotOccupiedBySanitary = joint.elements?.some((exEl) => {
              if (!SANITARY_ELEMENT_TYPES.includes(exEl.type)) return false;
              const exElSlotIndex = Math.max(
                0,
                Math.min(
                  numSlots - 1,
                  Math.floor(exEl.positionOnSegment * numSlots),
                ),
              );
              return exElSlotIndex === targetSlotIndex;
            });

            if (isTargetSlotOccupiedBySanitary) {
              toast.error("На цьому сегменті стыка вже є санитарний елемент");
              setElementTypeToPlace(null);
              return;
            }

            const allJoints = objectsOnCurrentFloor.filter(o => o.type === OBJECT_TYPES.DOCKING_JOINT);
            const allModules = objectsOnCurrentFloor.filter(o => o.type === OBJECT_TYPES.MODULE);

            if (isSanitaryJoint(joint, allModules)) {
              let correctPlacementSide = 1;

              const moduleA = allModules.find(m => m.id === joint.moduleA_id);
              const moduleB = allModules.find(m => m.id === joint.moduleB_id);

              if (moduleA?.subtype === 'sanitary' && moduleB?.subtype !== 'sanitary') {
                correctPlacementSide = 1;
              } else if (moduleB?.subtype === 'sanitary' && moduleA?.subtype !== 'sanitary') {
                correctPlacementSide = -1;
              }

              const isEdgeSegment = isEdgeSegmentInSanitaryJoint(
                joint,
                targetSlotIndex,
                correctPlacementSide,
                allJoints
              );

              if (isEdgeSegment) {
                toast.error("На крайні сэндвичі санитарного стыка нельзя ставить санитарні елементи");
                setElementTypeToPlace(null);
                return;
              }
            }
          } else {
            const isTargetSlotOccupied = joint.elements?.some((exEl) => {
              const exElSlotIndex = Math.max(
                0,
                Math.min(
                  numSlots - 1,
                  Math.floor(exEl.positionOnSegment * numSlots),
                ),
              );
              return exElSlotIndex === targetSlotIndex;
            });

            if (isTargetSlotOccupied) {
              if (
                elementTypeToPlace === OBJECT_TYPES.DOOR ||
                joint.elements?.find((el) => {
                  const elSlotIndex = Math.max(
                    0,
                    Math.min(
                      numSlots - 1,
                      Math.floor(el.positionOnSegment * numSlots),
                    ),
                  );
                  return elSlotIndex === targetSlotIndex;
                })?.type === OBJECT_TYPES.DOOR
              ) {
                toast.error(
                  elementTypeToPlace === OBJECT_TYPES.DOOR
                    ? t("toasts.doorPlacementOnOccupiedSandwichError")
                    : t("toasts.jointSegmentOccupied")
                );
                setElementTypeToPlace(null);
                return;
              }
            }
          }

          if (elementTypeToPlace === OBJECT_TYPES.DOOR) {
            const hasAdjacentDoor = joint.elements?.some((exEl) => {
              if (exEl.type !== OBJECT_TYPES.DOOR) return false;
              const exElSlotIndex = Math.max(
                0,
                Math.min(
                  numSlots - 1,
                  Math.floor(exEl.positionOnSegment * numSlots),
                ),
              );
              const distance = Math.abs(exElSlotIndex - targetSlotIndex);
              const isAdjacent = distance <= 1;

              return isAdjacent;
            });

            if (hasAdjacentDoor) {
              toast.error(t("toasts.doorTooCloseToAnotherDoor"));
              setElementTypeToPlace(null);
              return;
            }
          }
        }
      } else if (
        elementTypeToPlace === OBJECT_TYPES.DOOR &&
        segmentObject.elements?.length > 0
      ) {
        toast.error(t("toasts.doorPlacementOnOccupiedSandwichError"));
        setElementTypeToPlace(null);
        return;
      } else if (
        segmentObject.elements?.some((el) => el.type === OBJECT_TYPES.DOOR)
      ) {
        toast.error(t("toasts.elementInDoorwayError"));
        setElementTypeToPlace(null);
        return;
      }

      if (segmentObject.type === OBJECT_TYPES.DOCKING_JOINT) {
        if (segmentObject.displayMode === "passage") {
          toast.error(t("toasts.elementPlacementOnDisallowedWall"));
          setElementTypeToPlace(null);
          return;
        }

        if (segmentObject.displayMode === "double" &&
          (elementTypeToPlace === OBJECT_TYPES.WINDOW || elementTypeToPlace === OBJECT_TYPES.PANORAMIC_WINDOW)) {
          toast.error("Окна нельзя ставить на двойные стыки!");
          setElementTypeToPlace(null);
          return;
        }
      } else if (segmentObject.type === OBJECT_TYPES.WALL_SEGMENT) {
        if (segmentObject.isDocked) {
          toast.error(t("toasts.elementPlacementOnDisallowedWall"));
          setElementTypeToPlace(null);
          return;
        }
      }

      dispatch({
        type: "ADD_ELEMENT_TO_WALL",
        payload: {
          elementType: elementTypeToPlace,
          wallSegmentId: segmentId,
          positionOnSegment: finalPositionOnSegment, // --- ИЗМЕНЕНО ---
          clickSide: options.clickSide,
          segmentIndex: finalSegmentIndex,
        },
      });
      setElementTypeToPlace(null);
    },
    [
      elementTypeToPlace,
      state.currentFloorId,
      state.projectObjects,
      dispatch,
      setElementTypeToPlace,
      currentFloorIndex,
      t,
      checkForWindowNearby,
      objectsOnCurrentFloor,
      availableTemplates
    ],
  );

  return {
    handleWallSegmentClickForPlacement,
    checkForWindowNearby,
  };
};
